import { Injectable, TemplateRef } from "@angular/core";
import { Router } from "@angular/router";
import { BtnActionEnum, Menu, MenuItemType } from "@evc/web-components";
import { TranslateService } from "@ngx-translate/core";
import {
  AssetBuilderService,
  LiveAssetManagementService,
} from "@rvi/asset-modules";
import { BehaviorSubject } from "rxjs";
import { ConfigurationItem } from "../../../_objects/tenant-config";
import { RoutePage, RouteSettings } from "../../../app-routes.enum";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { FeatureFlagService } from "../../../_services/feature-flag/feature-flag.service";

type MenuItemExtended = MenuItemType & { isVisible: boolean };

@Injectable({
  providedIn: "root",
})
export class NavigationMenuService {
  constructor(
    private router: Router,
    private translate: TranslateService,
    private assetBuilderService: AssetBuilderService,
    private liveAssetService: LiveAssetManagementService,
    private networkStatusService: NetworkStatusService,
    private featureFlagService: FeatureFlagService
  ) {}

  private leftBarState = new BehaviorSubject<boolean>(true);

  public leftBarState$ = this.leftBarState.asObservable();
  public cta = new BehaviorSubject<TemplateRef<any> | null>(null);

  public toggleLeftBar(isLeftbarOpen: boolean): void {
    this.leftBarState.next(isLeftbarOpen);
  }

  private getDynamicLabelFor(key: string, tenantConfigs: ConfigurationItem[]) {
    return tenantConfigs?.find((config) => config.key == key)?.value?.toString() ?? "";
  }

  public buildLeftMenu = (
    isOnline: boolean,
    tenantConfig?: ConfigurationItem[]
  ): Menu[] => {
    const shouldShowSettingsMenu =
      this.router.url.includes(RoutePage.Settings) &&
      (this.router.url.includes(RouteSettings.Mark) ||
        this.router.url.includes(RouteSettings.Title) ||
        this.router.url.includes(RouteSettings.Tags) ||
        this.router.url.includes(RouteSettings.TenantConfig));

    const allMenuItems: MenuItemExtended[] = [
      {
        key: "inspections",
        text: this.translate.stream("topMenuBar.inspections"),
        isVisible: true,
        selected: this.router.url.includes(RoutePage.InspectionList),
        icon: "visibility-show",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.IndexPage]),
        },
      },
      {
        key: "reports",
        text: this.translate.stream("topMenuBar.reports"),
        isVisible: true,
        selected: this.router.url.includes(`/${RoutePage.InspectionReportList}`),
        icon: "report",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.InspectionReportList]),
        },
      },
      {
        key: "analytics",
        text: this.translate.stream("topMenuBar.analytics"),
        isVisible: isOnline,
        selected: this.router.url.includes(`/${RoutePage.AnalyticsPowerBiReport}`),
        icon: "circle-three-quarters",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.AnalyticsPowerBiReport]),
        },
      },
      {
        key: "asset-types",
        text: this.translate.stream("topMenuBar.asset-types"),
        isVisible: this.featureFlagService.isFlagEnabled("asset_module"),
        selected: this.router.url.includes(`/${RoutePage.AssetType}`),
        icon: "blueprint",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: async () => {
            this.router.navigate([RoutePage.AssetType]);
            await this.assetBuilderService.goToAssetTypesTable();
          },
        },
      },
      {
        key: "live-assets",
        text: this.translate.stream("topMenuBar.live-assets"),
        isVisible: this.featureFlagService.isFlagEnabled("asset_module"),
        selected: this.router.url.includes(`/${RoutePage.LiveAssets}`),
        icon: "live",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: async () => {
            this.router.navigate([RoutePage.LiveAssets]);
            await this.liveAssetService.goToLiveAssetTable();
          },
        },
      },
      {
        key: "tenant-config",
        text: this.translate.stream("settingTenant.configuration"),
        isVisible: shouldShowSettingsMenu,
        selected: this.router.url.includes(`/${RouteSettings.TenantConfig}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () =>
            this.router.navigate([
              RoutePage.Settings,
              RouteSettings.TenantConfig,
            ]),
        },
      },
      {
        key: "marks",
        text: this.getDynamicLabelFor("LabelOfMark", tenantConfig),
        isVisible: shouldShowSettingsMenu,
        selected: this.router.url.includes(`/${RouteSettings.Mark}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Mark]),
        },
      },
      {
        key: "titles",
        text: this.getDynamicLabelFor("LabelOfTitle", tenantConfig),
        isVisible: shouldShowSettingsMenu,
        selected: this.router.url.includes(`/${RouteSettings.Title}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Title]),
        },
      },
      {
        key: "tags",
        text: this.getDynamicLabelFor("LabelOfTags", tenantConfig),
        isVisible:
          shouldShowSettingsMenu &&
          (!this.networkStatusService.isOnPremise ||
            this.networkStatusService.isBrianMode),
        selected: this.router.url.includes(`/${RouteSettings.Tags}`),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Tags]),
        },
      },
    ];

    const items = allMenuItems.filter((item) => item.isVisible);

    return [{ items }];
  };
}
